import { z } from 'zod'

import {
  LocatorHint,
  ScenarioAction,
  ScenarioAssertion,
  WebTestTargetKind,
  type WebTestScenario
} from './scenario.ts'
import { sha256CanonicalJson } from './serialization.ts'

const identifier = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[a-zA-Z0-9._:-]+$/)

const compiledRelativeRoute = z
  .string()
  .min(1)
  .max(2_048)
  .refine((value) => value.startsWith('/') && !value.startsWith('//') && !value.includes('\\'), {
    message: 'Routes must start with one slash and cannot be absolute URLs'
  })

export const CompiledLocator = z
  .object({
    primary: LocatorHint,
    fallbacks: z.array(LocatorHint).max(4).default([]),
    rationale: z.string().trim().min(1).max(1_000)
  })
  .strict()
  .superRefine((locator, context) => {
    const candidates = [locator.primary, ...locator.fallbacks]
    for (let index = 1; index < candidates.length; index += 1) {
      if (locatorPriority(candidates[index]!) < locatorPriority(candidates[index - 1]!)) {
        context.addIssue({
          code: 'custom',
          message:
            'Locator candidates must stay accessibility-first: role, label, placeholder, text, test ID, then approved CSS',
          path: ['fallbacks', index - 1]
        })
      }
    }
  })
export type CompiledLocator = z.infer<typeof CompiledLocator>

export function locatorPriority(locator: z.infer<typeof LocatorHint>): number {
  return {
    role: 0,
    label: 1,
    placeholder: 2,
    text: 3,
    'test-id': 4,
    css: 5
  }[locator.by]
}

export const CompiledAction = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('navigate'), route: compiledRelativeRoute }).strict(),
  z
    .object({
      kind: z.literal('click'),
      target: CompiledLocator,
      button: z.enum(['left', 'right', 'middle']).optional()
    })
    .strict(),
  z
    .object({
      kind: z.literal('fill'),
      target: CompiledLocator,
      value: z.string().max(20_000),
      sensitive: z.boolean().default(false)
    })
    .strict(),
  z
    .object({
      kind: z.literal('select'),
      target: CompiledLocator,
      value: z.union([z.string(), z.array(z.string()).min(1)]),
      sensitive: z.boolean().default(false)
    })
    .strict(),
  z.object({ kind: z.literal('check'), target: CompiledLocator }).strict(),
  z.object({ kind: z.literal('uncheck'), target: CompiledLocator }).strict(),
  z
    .object({
      kind: z.literal('press'),
      target: CompiledLocator.optional(),
      key: z.string().min(1).max(200),
      modifiers: z
        .array(z.enum(['Alt', 'Control', 'Meta', 'Shift']))
        .max(4)
        .default([])
    })
    .strict(),
  z.object({ kind: z.literal('hover'), target: CompiledLocator }).strict(),
  z.object({ kind: z.literal('drag'), source: CompiledLocator, target: CompiledLocator }).strict(),
  z.object({ kind: z.literal('wait-visible'), target: CompiledLocator }).strict(),
  z.object({ kind: z.literal('wait-hidden'), target: CompiledLocator }).strict(),
  z.object({ kind: z.literal('wait-route'), route: compiledRelativeRoute }).strict(),
  z
    .object({
      kind: z.literal('wait-text'),
      target: CompiledLocator,
      value: z.string().min(1).max(20_000)
    })
    .strict(),
  z.object({ kind: z.literal('wait-network-idle') }).strict(),
  z.object({ kind: z.literal('upload'), target: CompiledLocator, fixture: identifier }).strict()
])
export type CompiledAction = z.infer<typeof CompiledAction>

export const CompiledAssertion = z
  .object({
    source: ScenarioAssertion,
    target: CompiledLocator.optional()
  })
  .strict()
export type CompiledAssertion = z.infer<typeof CompiledAssertion>

export const ActionPlanStep = z
  .object({
    id: identifier,
    scenarioStepId: identifier,
    description: z.string().trim().min(1).max(500),
    action: CompiledAction.optional(),
    assertions: z.array(CompiledAssertion).max(50).default([]),
    preconditions: z.array(z.string().trim().min(1).max(1_000)).max(20).optional(),
    expectedPostconditions: z.array(z.string().trim().min(1).max(1_000)).max(50).optional(),
    timeoutMs: z.number().int().min(100).max(120_000)
  })
  .strict()
  .refine((step) => step.action !== undefined || step.assertions.length > 0, {
    message: 'A compiled step must contain an action, at least one assertion, or both'
  })
export type ActionPlanStep = z.infer<typeof ActionPlanStep>

function locatorPreservesSource(
  compiled: z.infer<typeof CompiledLocator>,
  source: z.infer<typeof LocatorHint>
): boolean {
  return (
    compiled.fallbacks.length === 0 && JSON.stringify(compiled.primary) === JSON.stringify(source)
  )
}

function actionPreservesSource(
  compiled: z.infer<typeof CompiledAction>,
  source: z.infer<typeof ScenarioAction>
): boolean {
  switch (source.kind) {
    case 'navigate':
      return compiled.kind === 'navigate' && compiled.route === source.route
    case 'click':
      return (
        compiled.kind === 'click' &&
        compiled.button === source.button &&
        locatorPreservesSource(compiled.target, source.target)
      )
    case 'fill':
      return (
        compiled.kind === 'fill' &&
        compiled.value === source.value &&
        compiled.sensitive === (source.sensitive ?? false) &&
        locatorPreservesSource(compiled.target, source.target)
      )
    case 'select':
      return (
        compiled.kind === 'select' &&
        JSON.stringify(compiled.value) === JSON.stringify(source.value) &&
        compiled.sensitive === (source.sensitive ?? false) &&
        locatorPreservesSource(compiled.target, source.target)
      )
    case 'check':
    case 'uncheck':
    case 'hover':
      return compiled.kind === source.kind && locatorPreservesSource(compiled.target, source.target)
    case 'press':
      return (
        compiled.kind === 'press' &&
        compiled.key === source.key &&
        JSON.stringify(compiled.modifiers) === JSON.stringify(source.modifiers ?? []) &&
        (source.target === undefined
          ? compiled.target === undefined
          : compiled.target !== undefined && locatorPreservesSource(compiled.target, source.target))
      )
    case 'drag':
      return (
        compiled.kind === 'drag' &&
        locatorPreservesSource(compiled.source, source.source) &&
        locatorPreservesSource(compiled.target, source.target)
      )
    case 'wait':
      switch (source.for.kind) {
        case 'visible':
          return (
            compiled.kind === 'wait-visible' &&
            locatorPreservesSource(compiled.target, source.for.target)
          )
        case 'hidden':
          return (
            compiled.kind === 'wait-hidden' &&
            locatorPreservesSource(compiled.target, source.for.target)
          )
        case 'route':
          return compiled.kind === 'wait-route' && compiled.route === source.for.route
        case 'text':
          return (
            compiled.kind === 'wait-text' &&
            compiled.value === source.for.value &&
            locatorPreservesSource(compiled.target, source.for.target)
          )
        case 'network-idle':
          return compiled.kind === 'wait-network-idle'
      }
    case 'upload':
      return (
        compiled.kind === 'upload' &&
        compiled.fixture === source.fixture &&
        locatorPreservesSource(compiled.target, source.target)
      )
  }
}

export const ActionPlanLifecycleStep = z
  .object({
    id: identifier,
    scenarioStepId: identifier,
    description: z.string().trim().min(1).max(500),
    source: ScenarioAction,
    action: CompiledAction,
    preconditions: z.array(z.string().trim().min(1).max(1_000)).max(20).optional(),
    expectedPostconditions: z.array(z.string().trim().min(1).max(1_000)).max(50).optional(),
    timeoutMs: z.number().int().min(100).max(120_000)
  })
  .strict()
  .superRefine((step, context) => {
    if (!actionPreservesSource(step.action, step.source)) {
      context.addIssue({
        code: 'custom',
        message:
          'A lifecycle plan action must preserve the exact scenario-authored action without fallback locators',
        path: ['action']
      })
    }
  })
export type ActionPlanLifecycleStep = z.infer<typeof ActionPlanLifecycleStep>

export function describeActionPreconditions(action: CompiledAction): string[] {
  switch (action.kind) {
    case 'navigate':
      return ['The browser is connected to the declared target and the route stays on its origin.']
    case 'press':
      return action.target
        ? ['Exactly one element matches the committed locator and can receive keyboard input.']
        : ['The page is connected and can receive keyboard input.']
    case 'drag':
      return ['Exactly one source and one destination match their committed locators.']
    case 'wait-network-idle':
    case 'wait-route':
      return ['The page is connected and the scenario-wide deadline has not elapsed.']
    case 'click':
    case 'fill':
    case 'select':
    case 'check':
    case 'uncheck':
    case 'hover':
    case 'wait-visible':
    case 'wait-hidden':
    case 'wait-text':
    case 'upload':
      return ['Exactly one element matches the committed locator within the step timeout.']
  }
}

export function describeExpectedPostconditions(
  action: CompiledAction,
  assertions: CompiledAssertion[]
): string[] {
  const assertionConditions = assertions.map(({ source }) => {
    switch (source.kind) {
      case 'visible':
        return 'The asserted target is visible.'
      case 'hidden':
        return 'The asserted target is hidden or absent.'
      case 'enabled':
        return `The asserted target has enabled=${source.value}.`
      case 'checked':
        return `The asserted target has checked=${source.value}.`
      case 'text':
        return 'The asserted target text satisfies the authored expectation.'
      case 'value':
        return 'The asserted form value satisfies the authored expectation.'
      case 'count':
        return `The asserted target count is ${source.value}.`
      case 'route':
        return `The logical app route is ${source.route}.`
      case 'title':
        return 'The page title satisfies the authored expectation.'
      case 'accessibility':
        return 'The target accessibility snapshot satisfies the authored expectation.'
      case 'screenshot':
        return `The screenshot assertion "${source.name}" satisfies its configured threshold.`
      case 'console':
        return 'No unapproved console errors were observed.'
      case 'network':
        return 'No unapproved failed requests or error responses were observed.'
    }
  })
  if (assertionConditions.length > 0) return assertionConditions
  switch (action.kind) {
    case 'navigate':
      return [`The browser reaches ${action.route} on the approved origin.`]
    case 'wait-route':
      return [`The logical app route becomes ${action.route}.`]
    case 'wait-visible':
      return ['The target becomes visible.']
    case 'wait-hidden':
      return ['The target becomes hidden or absent.']
    case 'wait-text':
      return ['The target contains the authored non-sensitive text.']
    case 'wait-network-idle':
      return ['The page reaches the configured network-idle condition.']
    default:
      return ['The bounded action completes without a browser or locator error.']
  }
}

export const ActionPlan = z
  .object({
    schemaVersion: z.literal(1),
    scenarioId: identifier,
    scenarioHash: z.string().regex(/^[a-f0-9]{64}$/),
    planHash: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
    compilerVersion: z.string().min(1).max(100),
    executorProtocolVersion: z.literal(1),
    createdAt: z.string().datetime(),
    createdBy: z.enum(['ai-exploration', 'accepted-repair', 'hand-authored']),
    target: WebTestTargetKind,
    assumptions: z
      .object({
        viewport: z.object({ width: z.number().int(), height: z.number().int() }).strict(),
        locale: z.string().min(1),
        timezone: z.string().min(1)
      })
      .strict(),
    setup: z.array(ActionPlanLifecycleStep).max(50).optional(),
    steps: z.array(ActionPlanStep).min(1).max(500),
    cleanup: z.array(ActionPlanLifecycleStep).max(50).optional()
  })
  .strict()
  .superRefine((plan, context) => {
    const stepIds = new Set<string>()
    const phases = [
      ['setup', plan.setup ?? []],
      ['steps', plan.steps],
      ['cleanup', plan.cleanup ?? []]
    ] as const
    for (const [phase, steps] of phases) {
      for (const [stepIndex, step] of steps.entries()) {
        if (stepIds.has(step.id)) {
          context.addIssue({
            code: 'custom',
            message: `Duplicate compiled step id "${step.id}"`,
            path: [phase, stepIndex, 'id']
          })
        }
        stepIds.add(step.id)
        const action = step.action
        if ((action?.kind === 'fill' || action?.kind === 'select') && action.sensitive) {
          context.addIssue({
            code: 'custom',
            message:
              'Sensitive input values cannot be serialized into a committed plan; use an approved runtime fixture binding',
            path: [phase, stepIndex, 'action', 'value']
          })
        }
      }
    }
    for (const credential of findLikelyCredentials(plan)) {
      context.addIssue({
        code: 'custom',
        message: 'A recognizable credential or auth token cannot be committed in an action plan',
        path: credential.path
      })
    }
  })
export type ActionPlan = z.infer<typeof ActionPlan>

const credentialPatterns = [
  /-----BEGIN [A-Z ]*PRIVATE KEY-----/,
  /\bBearer\s+[A-Za-z0-9._~+/-]{12,}=*/i,
  /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{16,}\b/,
  /\bsk-[A-Za-z0-9_-]{16,}\b/,
  /\bgh[pousr]_[A-Za-z0-9]{20,}\b/,
  /\bgithub_pat_[A-Za-z0-9_]{20,}\b/,
  /\bglpat-[A-Za-z0-9_-]{20,}\b/,
  /\bxox[baprs]-[A-Za-z0-9-]{20,}\b/,
  /\bAIza[A-Za-z0-9_-]{20,}\b/,
  /\bvercel_[A-Za-z0-9_-]{20,}\b/,
  /\beyJ[A-Za-z0-9_-]{8,}\.eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/
]

export function containsLikelyCredential(value: string): boolean {
  return credentialPatterns.some((pattern) => pattern.test(value))
}

function findLikelyCredentials(
  value: unknown,
  path: Array<string | number> = []
): Array<{ path: Array<string | number> }> {
  if (typeof value === 'string') {
    return containsLikelyCredential(value) ? [{ path }] : []
  }
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => findLikelyCredentials(item, [...path, index]))
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) =>
      findLikelyCredentials(item, [...path, key])
    )
  }
  return []
}

export async function computeActionPlanHash(plan: ActionPlan): Promise<string> {
  const { planHash: _planHash, ...body } = plan
  return sha256CanonicalJson(body)
}

export async function actionPlanHashIsValid(plan: ActionPlan): Promise<boolean> {
  return plan.planHash !== undefined && plan.planHash === (await computeActionPlanHash(plan))
}

export interface PlanCompatibility {
  compatible: boolean
  reasons: string[]
}

export function checkPlanCompatibility(
  plan: ActionPlan,
  scenario: WebTestScenario,
  scenarioHash: string,
  target: z.infer<typeof WebTestTargetKind>,
  executorProtocolVersion = 1
): PlanCompatibility {
  const reasons: string[] = []
  if (plan.scenarioId !== scenario.id) reasons.push('scenario_id_changed')
  if (plan.scenarioHash !== scenarioHash) reasons.push('scenario_hash_changed')
  if (plan.target !== target) reasons.push('target_changed')
  if (!scenario.targets.includes(target)) reasons.push('target_not_declared')
  if (
    !sameOrderedIds(
      plan.setup?.map((step) => step.scenarioStepId) ?? [],
      scenario.setup?.map((step) => step.id) ?? []
    )
  ) {
    reasons.push('setup_steps_changed')
  } else if (!sameLifecycleSources(plan.setup ?? [], scenario.setup ?? [])) {
    reasons.push('setup_actions_changed')
  }
  if (
    !sameOrderedIds(
      plan.steps.map((step) => step.scenarioStepId),
      scenario.steps.map((step) => step.id)
    )
  ) {
    reasons.push('scenario_steps_changed')
  } else if (!sameScenarioAssertionSources(plan.steps, scenario.steps)) {
    reasons.push('scenario_assertions_changed')
  }
  if (
    !sameOrderedIds(
      plan.cleanup?.map((step) => step.scenarioStepId) ?? [],
      scenario.cleanup?.map((step) => step.id) ?? []
    )
  ) {
    reasons.push('cleanup_steps_changed')
  } else if (!sameLifecycleSources(plan.cleanup ?? [], scenario.cleanup ?? [])) {
    reasons.push('cleanup_actions_changed')
  }
  if (plan.executorProtocolVersion !== executorProtocolVersion) {
    reasons.push('executor_protocol_changed')
  }
  return { compatible: reasons.length === 0, reasons }
}

export function planAssertionSourcesArePreserved(
  before: z.infer<typeof ActionPlan>,
  after: z.infer<typeof ActionPlan>
): boolean {
  if (before.steps.length !== after.steps.length) return false
  const beforeByStep = new Map(before.steps.map((step) => [step.scenarioStepId, step]))
  return after.steps.every((step) => {
    const previous = beforeByStep.get(step.scenarioStepId)
    return (
      previous !== undefined &&
      previous.assertions.length === step.assertions.length &&
      step.assertions.every(
        (assertion, index) =>
          JSON.stringify(assertion.source) === JSON.stringify(previous.assertions[index]?.source)
      )
    )
  })
}

function sameOrderedIds(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function sameLifecycleSources(
  plan: Array<z.infer<typeof ActionPlanLifecycleStep>>,
  scenario: NonNullable<WebTestScenario['setup']>
): boolean {
  return (
    plan.length === scenario.length &&
    plan.every(
      (step, index) => JSON.stringify(step.source) === JSON.stringify(scenario[index]?.action)
    )
  )
}

function sameScenarioAssertionSources(
  plan: Array<z.infer<typeof ActionPlanStep>>,
  scenario: WebTestScenario['steps']
): boolean {
  return (
    plan.length === scenario.length &&
    plan.every((step, index) => {
      const authoredAssertions = scenario[index]?.assertions ?? []
      return (
        step.assertions.length === authoredAssertions.length &&
        step.assertions.every(
          (assertion, assertionIndex) =>
            JSON.stringify(assertion.source) === JSON.stringify(authoredAssertions[assertionIndex])
        )
      )
    })
  )
}
